import React, { useState, useEffect, useCallback } from 'react';
import auditLogsAPI from '../services/auditLogsAPI';
import { Button } from '../components/ui';
import {
  IconHistory, IconChevronLeft, IconChevronRight,
  IconRefresh, IconX,
} from '@tabler/icons-react';

const ACTION_LABELS = {
  create: 'Création',
  update: 'Modification',
  delete: 'Suppression',
  'mark-sent': 'Marquée envoyée',
  cancel: 'Annulation',
  convert: 'Conversion',
  close: 'Clôture',
  restore: 'Restauration',
};

const ENTITY_LABELS = {
  client: 'Client',
  invoice: 'Facture',
  payment: 'Paiement',
  quote: 'Devis',
  creditNote: 'Avoir',
  supplier: 'Fournisseur',
  purchaseInvoice: "Facture d'achat",
  purchasePayment: 'Paiement fournisseur',
  user: 'Utilisateur',
  task: 'Tâche',
  fiscalYear: 'Exercice',
  bankAccount: 'Compte bancaire',
  cashRegister: 'Caisse',
  treasuryTransaction: 'Transaction trésorerie',
};

const PAGE_SIZE = 50;

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    action: '',
    entityType: '',
  });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.action) params.action = filters.action;
      if (filters.entityType) params.entityType = filters.entityType;
      const data = await auditLogsAPI.findAll(params);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Erreur chargement audit logs:', err);
      setError(err.response?.data?.error || err.message || 'Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => { setPage(1); }, [filters.dateFrom, filters.dateTo, filters.action, filters.entityType]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({ dateFrom: '', dateTo: '', action: '', entityType: '' });
    setPage(1);
  };

  const hasActiveFilters = filters.dateFrom || filters.dateTo || filters.action || filters.entityType;

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 mb-1">Journal d&apos;audit</h1>
          <p className="text-gray-500 text-sm">
            Traçabilité complète des actions — {total} entrée{total > 1 ? 's' : ''}
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={fetchLogs}>
          <IconRefresh size={15} strokeWidth={1.6} />
          Actualiser
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">Date début</label>
            <input type="date" value={filters.dateFrom} onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">Date fin</label>
            <input type="date" value={filters.dateTo} onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">Action</label>
            <select value={filters.action} onChange={(e) => handleFilterChange('action', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm">
              <option value="">Toutes</option>
              {Object.entries(ACTION_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">Entité</label>
            <select value={filters.entityType} onChange={(e) => handleFilterChange('entityType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm">
              <option value="">Toutes</option>
              {Object.entries(ENTITY_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
        </div>
        {hasActiveFilters && (
          <button onClick={resetFilters}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors">
            <IconX size={14} strokeWidth={1.6} />
            Réinitialiser les filtres
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Chargement du journal d&apos;audit...</p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Date', 'Utilisateur', 'Rôle', 'Action', 'Entité', 'Détails'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-gray-500 text-sm">
                      Aucune entrée trouvée
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {formatDate(log.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-800 font-medium">
                        {log.userName || log.userId?.name || log.userId?.email || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {log.userRole || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          log.action === 'create' ? 'bg-green-100 text-green-800' :
                          log.action === 'delete' ? 'bg-red-100 text-red-800' :
                          log.action === 'update' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {ACTION_LABELS[log.action] || log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <span className="font-medium">{ENTITY_LABELS[log.entityType] || log.entityType}</span>
                        <span className="text-gray-500 ml-1">#{typeof log.entityId === 'string' ? log.entityId.slice(-6) : ''}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                        {log.entityLabel || '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-500">
                {total} entrée(s) — Page {page}/{totalPages}
              </p>
              <div className="flex gap-1">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                  className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                  <IconChevronLeft size={16} strokeWidth={1.6} />
                </button>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                  className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                  <IconChevronRight size={16} strokeWidth={1.6} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
